/**
 * Q&A queue panel - the host/presenter surface for raise hand
 * (docs/raise-hand.md). Pure DOM: no Babylon, no Photon. Network intent goes
 * through the handlers supplied by multiplayer.js via registerQaHandlers (the
 * registerPollHandlers pattern); the queue arrives through roomStore.
 *
 * Stable shell with in-place row repaints, so the wait timers ticking every
 * second never detach a button mid-click (the breakoutPanel discipline). Rows
 * are keyed by actorNr and only rebuilt when the membership or order changes.
 *
 * All user-controlled text (display names) renders via textContent, and names
 * resolve from the roster - never from anything a peer sent.
 */

import { TOKENS, createIcon } from './designSystem.js';
import { setPanelOpen, uiPanelsStore } from '../state/uiPanelsStore';
import { X, Hand, Mic, ArrowDown, Eraser, MapPin } from 'lucide';
import { roomStore } from '../state/roomStore';

let handlers = null; // supplied by multiplayer.js
let panel = null;
let overlay = null;
let bodyEl = null;
let emptyEl = null;
let listEl = null;
let footerEl = null;
let subscriptions = [];

/** @type {import('../utils/handQueueProtocol').QueueEntry[]} */
let queue = [];
/** @type {import('../utils/handQueueProtocol').QaState|null} */
let floor = null;
/** actorNr -> {row, waitEl, callBtn} for repaints that never rebuild. */
let rowEls = new Map();
let lastSignature = '';
let tickTimer = null;

/**
 * @param {Object} h - handlers + providers from multiplayer.js
 * @param {(actorNr: number) => void} h.onCallOn
 * @param {(actorNr: number) => void} h.onLowerOne
 * @param {() => void} h.onLowerAll
 */
export function registerQaHandlers(h) {
  handlers = h;
  ensureSubscriptions();
}

function ensureSubscriptions() {
  if (subscriptions.length) return;
  subscriptions.push(
    roomStore.subscribe(
      (s) => s.qa,
      (qa) => {
        queue = qa?.queue || [];
        floor = qa?.floor || null;
        if (isQaPanelOpen()) render();
      },
      { fireImmediately: true },
    ),
    roomStore.subscribe(
      (s) => s.roster,
      () => {
        if (isQaPanelOpen()) render();
      },
    ),
    uiPanelsStore.subscribe(
      (s) => s.open.qa,
      (visible) => {
        if (visible) {
          if (!handlers) return;
          const qa = roomStore.getState().qa;
          queue = qa?.queue || [];
          floor = qa?.floor || null;
          ensurePanel();
          overlay.style.display = 'flex';
          lastSignature = '';
          render();
          clearInterval(tickTimer);
          tickTimer = setInterval(repaintWaits, 1000);
        } else {
          clearInterval(tickTimer);
          tickTimer = null;
          if (overlay) overlay.style.display = 'none';
        }
      },
      { fireImmediately: true },
    ),
  );
}

/** Host action bar button hook (multiplayerUI.js). */
export function toggleQaPanel() {
  if (isQaPanelOpen()) closePanel();
  else openPanel();
}

/** Exported for tests/debug. */
export function isQaPanelOpen() {
  return uiPanelsStore.getState().open.qa;
}

function openPanel() {
  if (!handlers) return;
  ensurePanel();
  // Order matters: settingsMenu's pointerlockchange handler checks the flag,
  // so raise it before releasing the pointer or the pause menu opens too.
  setPanelOpen('qa', true);
  document.exitPointerLock?.();
}

function closePanel() {
  setPanelOpen('qa', false);
}

export function disposeQaPanel() {
  closePanel();
  subscriptions.forEach((unsubscribe) => unsubscribe());
  subscriptions = [];
  handlers = null;
}

function ensurePanel() {
  if (panel) return;
  // Full-screen scrim that dims the world and centers the modal card. Clicking
  // the backdrop (never the card) closes the panel; Esc is intentionally left
  // to the settings menu (hard constraint), so dismissal is backdrop + X only.
  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(2px)',
    zIndex: String(TOKENS.z.modal),
    pointerEvents: 'auto',
    padding: '16px',
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closePanel();
  });

  panel = document.createElement('div');
  panel.id = 'qa-panel';
  Object.assign(panel.style, {
    width: '340px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(18,18,22,0.94)',
    color: TOKENS.text.onDark,
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    boxShadow: TOKENS.shadow.md,
    backdropFilter: 'blur(4px)',
    fontFamily: TOKENS.font,
    fontSize: '13px',
    pointerEvents: 'auto',
    overflow: 'hidden',
  });
  // Clicks inside the card must never reach the backdrop's close handler.
  panel.addEventListener('click', (e) => e.stopPropagation());

  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    flex: 'none',
  });
  const title = document.createElement('div');
  Object.assign(title.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: TOKENS.weight.semibold,
  });
  title.appendChild(createIcon(Hand, { size: 16 }));
  const titleText = document.createElement('span');
  titleText.textContent = 'Q&A queue';
  title.appendChild(titleText);
  const closeBtn = iconButton(X, 'Close');
  closeBtn.onclick = () => closePanel();
  header.append(title, closeBtn);

  bodyEl = document.createElement('div');
  Object.assign(bodyEl.style, {
    flex: '1',
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  });
  // Panel keys must never drive the avatar (H, WASD) while the panel is up.
  bodyEl.addEventListener('keydown', (e) => e.stopPropagation());
  bodyEl.addEventListener('keyup', (e) => e.stopPropagation());

  emptyEl = document.createElement('div');
  emptyEl.textContent = 'Nobody has their hand up yet.';
  Object.assign(emptyEl.style, {
    color: TOKENS.text.muted,
    padding: '18px 0',
    textAlign: 'center',
  });

  listEl = document.createElement('div');
  Object.assign(listEl.style, { display: 'flex', flexDirection: 'column', gap: '6px' });

  footerEl = document.createElement('div');
  Object.assign(footerEl.style, {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '10px 14px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    flex: 'none',
  });
  const lowerAll = actionButton('Lower all hands', { icon: Eraser });
  lowerAll.onclick = () => handlers?.onLowerAll();
  footerEl.appendChild(lowerAll);

  bodyEl.append(emptyEl, listEl);
  panel.append(header, bodyEl, footerEl);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}

function nameFor(actorNr) {
  const roster = roomStore.getState().roster.entries;
  const match = roster.find((entry) => entry && entry.actorNr === actorNr);
  return match?.name || `User ${actorNr}`;
}

/** Membership + order + who holds the floor. Wait times are repainted separately. */
function signatureOf() {
  return `${queue.map((e) => e.actorNr + (e.coLevel ? '' : '*')).join(',')}|${floor?.speakingActorNr ?? ''}`;
}

function render() {
  const signature = signatureOf();
  if (signature === lastSignature) {
    repaintWaits();
    return;
  }
  lastSignature = signature;

  emptyEl.style.display = queue.length ? 'none' : 'block';
  footerEl.style.display = queue.length ? 'flex' : 'none';
  rowEls = new Map();
  listEl.replaceChildren(...queue.map(buildRow));
  repaintWaits();
}

function buildRow(entry) {
  const speaking = floor?.speakingActorNr === entry.actorNr;

  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: TOKENS.radius.md,
    background: speaking ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
    border: speaking ? '1px solid rgba(34,197,94,0.5)' : '1px solid transparent',
  });

  const place = document.createElement('span');
  place.textContent = String(entry.position);
  Object.assign(place.style, {
    minWidth: '18px',
    color: TOKENS.text.muted,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  });

  const identity = document.createElement('div');
  Object.assign(identity.style, { flex: '1', minWidth: '0' });

  const name = document.createElement('div');
  name.textContent = nameFor(entry.actorNr);
  Object.assign(name.style, {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  const meta = document.createElement('div');
  Object.assign(meta.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: TOKENS.text.muted,
    fontSize: '11px',
  });
  const waitEl = document.createElement('span');
  meta.appendChild(waitEl);
  if (!entry.coLevel) {
    // Cross-level participants are at volume 0, so calling on them would hand
    // the floor to silence. Say so plainly rather than hiding them.
    const elsewhere = document.createElement('span');
    Object.assign(elsewhere.style, { display: 'inline-flex', alignItems: 'center', gap: '3px' });
    elsewhere.appendChild(createIcon(MapPin, { size: 11 }));
    const label = document.createElement('span');
    label.textContent = 'in another level';
    elsewhere.appendChild(label);
    meta.append(document.createTextNode('-'), elsewhere);
  }
  identity.append(name, meta);

  const actions = document.createElement('div');
  Object.assign(actions.style, { display: 'flex', gap: '4px', flex: 'none' });

  const callBtn = actionButton(speaking ? 'Speaking' : 'Call on', {
    primary: !speaking,
    icon: Mic,
    small: true,
  });
  if (speaking || !entry.coLevel) {
    callBtn.disabled = true;
    callBtn.style.opacity = '0.5';
    callBtn.style.cursor = 'default';
    callBtn.title = speaking
      ? 'They have the floor. It clears when they lower their hand.'
      : 'They are in another level, where nobody here can hear them.';
  } else {
    callBtn.onclick = () => handlers?.onCallOn(entry.actorNr);
  }

  const lowerBtn = iconButton(ArrowDown, `Lower ${nameFor(entry.actorNr)}'s hand`);
  lowerBtn.onclick = () => handlers?.onLowerOne(entry.actorNr);

  actions.append(callBtn, lowerBtn);
  row.append(place, identity, actions);
  rowEls.set(entry.actorNr, { waitEl });
  return row;
}

/** In-place only: never rebuilds, so a 1Hz tick can't swallow a click. */
function repaintWaits() {
  const now = Date.now();
  for (const entry of queue) {
    const els = rowEls.get(entry.actorNr);
    if (!els) continue;
    els.waitEl.textContent = `waiting ${formatWait(now - entry.raisedAt)}`;
  }
}

function formatWait(ms) {
  const secs = Math.max(0, Math.round(ms / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
}

// --- Shared small builders (the pollPanel set) ---

function iconButton(icon, titleText) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = titleText;
  Object.assign(btn.style, {
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px',
  });
  btn.appendChild(createIcon(icon, { size: 16 }));
  return btn;
}

function actionButton(label, { primary = false, icon = null, small = false } = {}) {
  const btn = document.createElement('button');
  btn.type = 'button';
  Object.assign(btn.style, {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: 'none',
    borderRadius: TOKENS.radius.md,
    padding: small ? '4px 8px' : '6px 12px',
    fontFamily: 'inherit',
    fontSize: small ? '12px' : '13px',
    fontWeight: TOKENS.weight.medium,
    cursor: 'pointer',
    color: '#fff',
    background: primary ? TOKENS.action.primary.background : 'rgba(255,255,255,0.12)',
  });
  if (icon) btn.appendChild(createIcon(icon, { size: small ? 12 : 14 }));
  const span = document.createElement('span');
  span.textContent = label;
  btn.appendChild(span);
  return btn;
}
